import { FC, useMemo } from "react";

import { ISubscription, SubscriptionType } from "Bands/types";
import { copy } from "shared/assets";
import { User } from "shared/assets/icons";
import { Button, Loader, Title } from "shared/components";
import { Spacer } from "shared/layout";
import { Icon, MutedText } from "shared/styles";
import { colors } from "shared/styles/constants";
import { formatNumber } from "shared/utils";

import {
  SubscriptionCardContainer,
  SubscriptionCardHeader,
  SubscriptionCardContent,
  SubscriptionCardHeaderPrice,
} from "./styles";

const planCopy = copy.subscription.plan;
const summaryCopy = copy.subscription.summary;

export enum SubscriptionCardType {
  "default",
  "current",
  "next",
  "canceled",
}

export interface SubscriptionCardProps {
  type?: SubscriptionCardType;
  subscription: ISubscription;
  isLoading?: boolean;
  onSelect: (type: SubscriptionType) => void;
}

export const SubscriptionCard: FC<SubscriptionCardProps> = ({
  subscription: { title, plan, amount, billingTerm, info },
  type = "default",
  isLoading,
  onSelect,
}) => {
  const buttonCopy = useMemo(() => {
    if (type === SubscriptionCardType.current) return planCopy.current;
    if (type === SubscriptionCardType.next) return planCopy.next;

    return planCopy.cta;
  }, [type]);

  const isDisabled = useMemo(
    () =>
      type === SubscriptionCardType.current ||
      type === SubscriptionCardType.next,
    [type]
  );

  return (
    <>
      <SubscriptionCardContainer isDisabled={isDisabled}>
        <SubscriptionCardHeader size="s">
          <Icon size="xxl" color={colors.grey5} as={User} />
          <Title title={title} asComponent="h3" />
          <SubscriptionCardHeaderPrice>
            {amount
              ? `€ ${formatNumber(amount, {
                  minimumFractionDigits: 2,
                })} / ${billingTerm}`
              : summaryCopy.onDemand}
          </SubscriptionCardHeaderPrice>
          {amount && <MutedText>{planCopy.exclVat}</MutedText>}
        </SubscriptionCardHeader>
        <SubscriptionCardContent size="l">
          <Spacer size="s" align="center">
            {info.map((item) => (
              <MutedText>{item}</MutedText>
            ))}
          </Spacer>
          <Button
            onClick={() => onSelect(plan)}
            isFullWidth
            data-testid={`${plan}_subscription_button`}
            disabled={isDisabled}
          >
            <span>{buttonCopy}</span>
            {isLoading && <Loader size="s" />}
          </Button>
        </SubscriptionCardContent>
      </SubscriptionCardContainer>
    </>
  );
};
